import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Music, CheckCircle2, Eye, EyeOff, Smartphone } from 'lucide-react';
import { useDesign } from '@/contexts/DesignContext';
import { StudioAuthShell, StudioGateCard, StudioNotice } from '@/components/studio/auth/StudioAuthParts';

function ResetPasswordPage({ onComplete }) {
  const { isStudio } = useDesign();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // null = checking, '' = no session (dead link), string = account email
  const [sessionEmail, setSessionEmail] = useState(null);

  // The form must only operate on a verified recovery session — updateUser()
  // changes the password of WHATEVER session is active. Showing the account
  // email makes a wrong-account situation visible instead of silent.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSessionEmail(data?.session?.user?.email || '');
    }).catch(() => setSessionEmail(''));
  }, []);

  // Detect mobile phone / small tablet — these users should go back to the native app
  // Large tablets (≥1024px) and desktops get the normal web flow (admins use web)
  const isMobileDevice = useMemo(() => {
    const ua = navigator.userAgent || '';
    const isMobileOS = /iPhone|iPod|Android/i.test(ua);
    const isSmallScreen = window.innerWidth < 1024;
    return isMobileOS && isSmallScreen;
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      // Final guard: never update without a live session.
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session) {
        throw new Error('Your reset link has expired. Please request a new one.');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);

      // Desktop/large tablet: auto-redirect to web app after 2 seconds
      // Mobile: stay on success screen — user taps "Open App" button
      if (!isMobileDevice) {
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message || 'Unable to reset password. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  // ---- Studio (doc 119 §24, mock frames 19 and 27) ----
  if (isStudio) {
    if (success) {
      return (
        <StudioAuthShell>
          <StudioGateCard
            icon={CheckCircle2}
            kind="ok"
            title="Password reset successful"
            sentence={isMobileDevice
              ? 'Open the app and sign in with your new password.'
              : 'Redirecting you to sign in…'}
          >
            {isMobileDevice && (
              <a href="worshipos://" className="st-au-btn st-au-pri st-au-lg st-au-full" data-testid="open-app-btn">
                <Smartphone />
                Open Worship OS
              </a>
            )}
          </StudioGateCard>
        </StudioAuthShell>
      );
    }
    return (
      <StudioAuthShell>
        <div className="st-au-card">
          <div className="st-au-head">
            <h1 className="st-au-t">Set new password</h1>
            <p className="st-au-s">
              {sessionEmail
                ? <>Setting a new password for <b data-testid="reset-account-email">{sessionEmail}</b></>
                : 'Enter your new password below'}
            </p>
          </div>
          {sessionEmail === '' ? (
            <div data-testid="reset-link-expired">
              <StudioNotice kind="warn">
                This reset link has expired or was already used. Please return to the sign-in page and request a new one.
              </StudioNotice>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="st-au-form">
              {error && <StudioNotice kind="bad">{error}</StudioNotice>}
              <div className="st-au-field">
                <Label htmlFor="new-password">New password</Label>
                <div className="st-au-fwrap">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="st-au-in st-au-trail"
                  />
                  <button type="button" onClick={() => setShowPassword(p => !p)} className="st-au-eye" tabIndex={-1} data-testid="reset-password-toggle">
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>
              <div className="st-au-field">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <div className="st-au-fwrap">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="st-au-in st-au-trail"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(p => !p)} className="st-au-eye" tabIndex={-1} data-testid="reset-confirm-password-toggle">
                    {showConfirmPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className={`st-au-btn st-au-pri st-au-lg st-au-full${loading ? ' st-au-busy' : ''}`}
                disabled={loading}
              >
                {loading ? <><span className="st-au-spin" /><span>Updating…</span></> : 'Update password'}
              </Button>
            </form>
          )}
        </div>
      </StudioAuthShell>
    );
  }

  if (success) {
    // Mobile: show "Open App" screen
    if (isMobileDevice) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-2xl border-0 bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm">
            <CardContent className="p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">Password Reset Successful!</h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">Open the app and sign in with your new password.</p>
              <a
                href="worshipos://"
                className="inline-flex items-center justify-center gap-2 w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg transition-all"
                data-testid="open-app-btn"
              >
                <Smartphone className="w-5 h-5" />
                Open Worship OS
              </a>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Desktop: auto-redirect (existing behavior)
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">Password Reset Successful!</h2>
            <p className="text-slate-500 dark:text-slate-400">Redirecting you to sign in...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0 bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Music className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Set New Password
          </CardTitle>
          <CardDescription className="text-slate-500 dark:text-slate-400">
            {sessionEmail
              ? <>Setting a new password for <span className="font-medium text-slate-700 dark:text-slate-200" data-testid="reset-account-email">{sessionEmail}</span></>
              : 'Enter your new password below'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionEmail === '' ? (
            <div className="p-4 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-amber-200 dark:border-amber-500/40" data-testid="reset-link-expired">
              This reset link has expired or was already used. Please return to the sign-in page and request a new one.
            </div>
          ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-100">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  tabIndex={-1}
                  data-testid="reset-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  tabIndex={-1}
                  data-testid="reset-confirm-password-toggle"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-lg"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPasswordPage;
